import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { authApi } from "../../api/auth";
import { useBranding } from "../../shared/branding";
import { AuthShell } from "../../shared/AuthShell";
import { errorMessage } from "../../shared/errors";
import { useMe } from "../../shared/hooks/useMe";
import { Alert, Button } from "../../ui";

const SCOPE_TEXT: Record<string, string> = {
  openid: "Confirm your identity",
  email: "See your email address",
  profile: "See your name and locale",
  offline_access: "Stay signed in without asking again",
  org: "See which organization you act for",
};

/** Product scopes read as `<product>.tools.read`; say what they allow, not what they are. */
function scopeText(scope: string, product: string): string {
  if (SCOPE_TEXT[scope]) return SCOPE_TEXT[scope];
  if (scope.endsWith(".tools.read"))
    return `Read your ${product} data through assistant tools`;
  if (scope.endsWith(".tools.write"))
    return `Act in ${product} through assistant tools`;
  return scope;
}

export function ConsentPage() {
  const [search] = useSearchParams();
  const clientId = search.get("client_id") ?? "";
  const scope = search.get("scope") ?? "";
  const returnTo = search.get("return_to") ?? "";
  const branding = useBranding(clientId || null);
  const me = useMe();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const decide = async (decision: "allow" | "deny") => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const result = await authApi.consent({
        client_id: clientId,
        scope,
        return_to: returnTo,
        decision,
      });
      if (result.redirect_to) {
        // Stay busy: leaving the page takes a moment (the return URL redirects on to the
        // client), and a button that says "Allow" again looks like the click was lost.
        window.location.assign(result.redirect_to);
        return;
      }
      setError("This authorization request is no longer valid.");
    } catch (err) {
      setError(errorMessage(err));
    }
    setBusy(false);
  };

  return (
    <AuthShell
      branding={branding}
      title={`Allow ${branding.name} to access your account?`}
    >
      {error ? <Alert kind="error">{error}</Alert> : null}
      {me.data ? (
        <p className="mb-3 text-sm text-slate-600">
          Signed in as {me.data.email}
        </p>
      ) : null}
      <ul className="mb-4 list-disc pl-5 text-sm">
        {scope
          .split(" ")
          .filter(Boolean)
          .map((s) => (
            <li key={s}>{scopeText(s, branding.name)}</li>
          ))}
      </ul>
      <div className="flex gap-2">
        <Button onClick={() => decide("allow")} loading={busy}>
          Allow
        </Button>
        <Button
          variant="secondary"
          onClick={() => decide("deny")}
          disabled={busy}
        >
          Deny
        </Button>
      </div>
    </AuthShell>
  );
}
